"use client";

import pendingSearch from "@/lib/pendingSearch";
import { areArraysEqual } from "@/lib/utils";
import type { CustomLocation, MapAreaId } from "@/utils/location";
import { useCallback } from "react";

export interface PendingChangesParams {
  currentArea: MapAreaId;
  currentActivities: string[];
  /** Current custom location from URL params */
  currentCustomLocation?: CustomLocation | null;
}

/**
 * Hook to detect if there are pending changes in search parameters.
 * Compares pending search state (area, custom location, and activities) against current values.
 *
 * @param params - Current area, custom location, and activities to compare against
 * @returns Object with methods to check for pending changes
 */
export function usePendingChangesDetection({
  currentArea,
  currentActivities,
  currentCustomLocation,
}: PendingChangesParams) {
  /**
   * Checks if there are any pending changes (area, custom location, or activities).
   * Used when closing selectors to determine if search button should stay expanded.
   *
   * @returns true if area, custom location, or activities have pending changes
   */
  const hasPendingChanges = useCallback((): boolean => {
    const {
      area: pendingArea,
      activities: pendingActivities,
      customLocation: pendingCustomLocation,
    } = pendingSearch.get();

    // Check if area changed (either to a new area or cleared due to custom location)
    const hasAreaChanges =
      pendingArea !== undefined && pendingArea !== currentArea;

    // Check if custom location changed
    const hasCustomLocationChanges =
      pendingCustomLocation !== undefined &&
      !areCustomLocationsEqual(pendingCustomLocation, currentCustomLocation);

    // Check if switching between area and custom location
    const switchingToCustomLocation =
      pendingCustomLocation !== undefined && currentCustomLocation === null;
    const switchingToArea =
      pendingArea !== undefined && currentCustomLocation !== null;

    const hasActivityChanges =
      pendingActivities !== undefined &&
      !areArraysEqual(pendingActivities, currentActivities);

    return (
      hasAreaChanges ||
      hasCustomLocationChanges ||
      switchingToCustomLocation ||
      switchingToArea ||
      hasActivityChanges
    );
  }, [currentArea, currentActivities, currentCustomLocation]);

  /**
   * Checks if a specific area change would constitute a pending change.
   *
   * @param newArea - The new area to check against current area
   * @returns true if newArea differs from currentArea
   */
  const hasAreaChange = useCallback(
    (newArea: MapAreaId): boolean => {
      return newArea !== currentArea;
    },
    [currentArea],
  );

  /**
   * Checks if specific activity changes would constitute a pending change.
   *
   * @param newActivities - The new activities to check against current activities
   * @returns true if newActivities differ from currentActivities
   */
  const hasActivityChange = useCallback(
    (newActivities: string[]): boolean => {
      return !areArraysEqual(newActivities, currentActivities);
    },
    [currentActivities],
  );

  return {
    hasPendingChanges,
    hasAreaChange,
    hasActivityChange,
  };
}

/**
 * Compare two custom locations for equality
 */
function areCustomLocationsEqual(
  a: CustomLocation | null | undefined,
  b: CustomLocation | null | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.lat === b.lat && a.lon === b.lon && a.displayName === b.displayName;
}
